import { DEPLOY_ENVS } from '@trellis/shared'
import { z } from 'zod'

const EnvSchema = z.object({
  /** Base URL for the generated API client. Must not include a trailing slash. */
  VITE_API_BASE_URL: z.url(),

  /**
   * Full backend URL passed to better-auth's createAuthClient({ baseURL }). better-auth
   * appends /api/auth automatically. Must not include a trailing slash.
   */
  VITE_APP_URL: z.url(),

  /**
   * Base URL for the admin API, used by self-service signup flow. Defaults to
   * localhost:3003 for local development.
   */
  VITE_ADMIN_API_BASE_URL: z.url().default('http://localhost:3003'),

  /**
   * Deployment tier, baked in at build time via Docker build-arg. Gates dev-only UI (e.g.
   * the Dev Mode settings page) — defaults to "local" for `pnpm dev`.
   */
  VITE_DEPLOY_ENV: z.enum(DEPLOY_ENVS).default('local'),

  /**
   * Shared "frontend" Sentry project DSN — safe to expose client-side. Leave unset to
   * disable Sentry entirely (e.g. local dev).
   */
  VITE_SENTRY_DSN: z.string().optional(),

  /**
   * Google Tag Manager container ID (GTM-XXXXXXX), baked in at build time. Leave unset to
   * disable product analytics entirely — no script loads and every event is a no-op.
   */
  VITE_GTM_CONTAINER_ID: z.string().optional(),

  /** Service-prefixed release identifier, e.g. platform-app@<git-sha>, set at build time. */
  VITE_SENTRY_RELEASE: z.string().default('platform-app@local'),

  /**
   * Human-readable build version via `git describe --tags --always --match 'v*'`, set at
   * build time. Displayed in the sidebar footer so support engineers can identify a build
   * from a bug report.
   */
  VITE_APP_VERSION: z.string().default('local'),

  /**
   * ISO date (YYYY-MM-DD) after which unenrolled users are redirected to /2fa-setup. Must
   * match the backend's TWO_FACTOR_ENFORCEMENT_DATE — the frontend only uses this to
   * decide when to show the grace-period banner vs. hard-redirect; the backend
   * SessionGuard is the actual enforcement point. Unset means never enforced.
   */
  VITE_TWO_FACTOR_ENFORCEMENT_DATE: z.iso.date().optional(),

  /**
   * VAPID public key passed to `PushManager.subscribe({ applicationServerKey })`. Must
   * match the backend's VAPID_PUBLIC_KEY. Unset disables the push opt-in UI
   * (`usePushSubscription`'s `isSupported` reports `false`).
   */
  VITE_VAPID_PUBLIC_KEY: z.string().optional(),

  /**
   * Shows the TanStack Router devtools panel in dev builds. Set to "false" in
   * `.env.local` to opt out locally; has no effect outside `pnpm dev` (see __root.tsx).
   */
  VITE_SHOW_ROUTER_DEVTOOLS: z.stringbool().default(true),
})

const parsed = EnvSchema.safeParse(import.meta.env)

if (!parsed.success) {
  const errors = z.treeifyError(parsed.error).errors

  throw new Error(
    `Missing or invalid environment variables:\n ${JSON.stringify(errors, null, 2)}\n
    Check apps/platform-app/.env.local — see the Doppler Secrets Management spec on Confluence for the full variable reference.`,
  )
}

export const env = parsed.data
